/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomExportButton.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { Box, Menu, MenuItem } from "@mui/material";
import * as XLSX from "xlsx";
import React, { useState } from "react";
import PropTypes from "prop-types";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";


const sanitizeExportValue = (value) => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value.map(sanitizeExportValue);
  }
  if (typeof value === "object") {
    const cleanedObj = {};
    for (const [k, v] of Object.entries(value)) {
      if (k === "legal_standard_details") {
        if (!v || (typeof v === "object" && (!v?.name || !String(v.name).trim()))) {
          cleanedObj[k] = "";
          continue;
        }
      }
      cleanedObj[k] = sanitizeExportValue(v);
    }
    return cleanedObj;
  }
  return value;
};

function CustomExportButton({ fileName, exportDataExcel, exportDataJson }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportExcel = () => {
    handleClose();

    if (!exportDataExcel || Object.keys(exportDataExcel).length === 0) return;

    const sanitizedExcelData = sanitizeExportValue(exportDataExcel);
    const wb = XLSX.utils.book_new();

    for (const [sheetName, sheetData] of Object.entries(sanitizedExcelData)) {
      if (Array.isArray(sheetData) && sheetData.length > 0) {
        const processedSheetData = sheetData.map((row) => {
          const formattedRow = {};
          for (const [key, value] of Object.entries(row || {})) {
            if (value !== null && typeof value === "object") {
              formattedRow[key] = JSON.stringify(value);
            } else {
              formattedRow[key] = value;
            }
          }
          return formattedRow;
        });
        const worksheet = XLSX.utils.json_to_sheet(processedSheetData);
        XLSX.utils.book_append_sheet(wb, worksheet, sheetName);
      }
    }

    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };

  const handleExportJson = () => {
    handleClose();
    if (!exportDataJson || Object.keys(exportDataJson).length === 0) return;

    const sanitizedJsonData = sanitizeExportValue(exportDataJson);
    const jsonData = JSON.stringify(sanitizedJsonData, null, 2); // Pretty-print JSON
    const blob = new Blob([jsonData], { type: "application/json" });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = `${fileName}.json`; // Save file with ".json" extension
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Box>
      <CustomButtonV2
        size={"small"}
        btnName={"Export"}
        startIcon={<FileDownloadIcon />}
        handleOnClick={(e) => setAnchorEl(e.currentTarget)}
        btnStyle={{
          ml: 2,
        }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleExportJson()}>Export JSON</MenuItem>
        <MenuItem onClick={() => handleExportExcel()}>Export Excel</MenuItem>
      </Menu>
    </Box>
  );
}

CustomExportButton.propTypes = {
  fileName: PropTypes.string,
  exportDataExcel: PropTypes.object,
  exportDataJson: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default CustomExportButton;
